import { auth, apiBase, db, getActiveTenantCode } from './firebase';
import { collection, getDocs, limit as fsLimit, query, where } from 'firebase/firestore';
import type {
  KnownIssue,
  Ticket,
  TicketPriority,
  TicketSource,
  TicketStatus,
  WritableTicketSource,
} from './types';
import { ApiError, malformedJsonError, toApiError, xhrApiError } from './api-error';

/** Active-tenant header injected on every agent/admin request. Empty when unknown. */
function tenantHeaders(): Record<string, string> {
  const code = getActiveTenantCode();
  return code ? { 'X-Tenant-Code': code } : {};
}

/**
 * Success-path body read. A 204 (or empty body) is `undefined`, and a 200 that
 * isn't JSON — a proxy or login page — becomes a clean ApiError instead of a
 * raw SyntaxError with the document in its message.
 */
async function parseJsonBody<T>(res: Response, path: string): Promise<T> {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw malformedJsonError(res.status, path, text);
  }
}

export async function authedFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  const res = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...tenantHeaders(),
      ...(init.headers || {}),
    },
  });
  if (!res.ok) throw await toApiError(res, path);
  return parseJsonBody<T>(res, path);
}

export async function downloadAuthed(path: string, filename: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  const res = await fetch(`${apiBase}${path}`, {
    headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
  });
  if (!res.ok) throw await toApiError(res, path);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface ReplyInput {
  tenantCode: string;
  chatId: string;
  agentUid: string;
  content: string;
  internal?: boolean;
  attachmentPath?: string;
  attachmentMimeType?: string;
  attachmentName?: string;
  quotedMessageId?: string;
  quotedPreview?: string;
  subject?: string;
  template?: { name: string; language: string; parameters?: string[] };
  messengerTag?: 'CONFIRMED_EVENT_UPDATE' | 'POST_PURCHASE_UPDATE' | 'ACCOUNT_UPDATE' | 'HUMAN_AGENT';
  /** The AI draft the agent used/approved (F5 approve→memory + Use&edit capture). */
  usedDraft?: string;
}

/**
 * What happened on the external leg of a send.
 *   sent    – a provider accepted it (`channelMessageId` is its id).
 *   failed  – it was handed to a provider and refused / errored.
 *   denied  – the outbound policy refused it (consent, 24h window, rate cap);
 *             nothing was handed to a provider at all.
 *   skipped – the channel has no external leg (web/api): the widget's own
 *             Firestore listener IS the delivery, so there is nothing to report.
 */
export type DispatchStatus = 'sent' | 'failed' | 'denied' | 'skipped';

export interface DispatchSummary {
  status: DispatchStatus;
  /** Human-readable provider/policy reason. Present on everything but 'sent'. */
  reason?: string;
  /** Provider or policy code — Meta's '190', a PolicyDenyCode, 'MISSING_CONFIG'. */
  code?: string;
  channelMessageId?: string;
  channel?: string;
}

/**
 * POST /agent/message. `dispatch` is absent for internal notes (nothing is sent)
 * and on any backend older than the delivery-truth change.
 *
 * It is the only SYNCHRONOUS signal that a reply actually reached a provider,
 * so every caller must read it. This type used to be `{ messageId: string }`,
 * which meant the browser parsed the outcome and threw it away — bekesher's
 * WhatsApp token expired on 2026-07-25 and every agent reply for the following
 * 19 days looked like it had been sent.
 */
export interface ReplyResult {
  messageId: string;
  dispatch?: DispatchSummary;
}

/**
 * Plain-language remedy for a provider/policy code, where we have one. Null when
 * the code is unknown — the raw `reason` is then the whole story, and inventing
 * a remedy for a code we do not recognise is worse than saying nothing.
 */
const DISPATCH_CODE_HINTS: Record<string, string> = {
  // Meta OAuthException 190 / 102: the WhatsApp access token expired or was
  // revoked. Nothing anyone types goes out until the channel is reconnected.
  '190': 'The WhatsApp connection has expired. An admin has to reconnect it on this inbox — until then nothing sends on WhatsApp.',
  '102': 'The WhatsApp session is no longer valid. An admin has to reconnect the channel on this inbox.',
  MISSING_CONFIG: 'This channel is not fully configured on the inbox, so there was nowhere to send it.',
  MISSING_ADDRESS: 'We have no address for this contact on this channel.',
  NO_OPTIN: 'This contact has not opted in to messages on this channel.',
  CONTACT_SUPPRESSED: 'This contact is suppressed (unsubscribed or hard-bounced).',
  OUTSIDE_WINDOW_NO_FALLBACK: 'The 24-hour reply window has closed — send an approved template instead.',
  TEMPLATE_NOT_APPROVED: 'That template is not approved by the provider.',
  TEMPLATE_REQUIRED: 'The provider requires an approved template for this message.',
  RATE_LIMIT: 'The daily cap for this contact and channel has been reached.',
  CANNOT_INITIATE_FB: 'Messenger does not allow starting a conversation with this person.',
};

export function dispatchCodeHint(code?: string | null): string | null {
  return code ? (DISPATCH_CODE_HINTS[code] ?? null) : null;
}

/** True when the send did not reach the customer and the agent has to know. */
export function dispatchFailed(d?: DispatchSummary | null): boolean {
  return !!d && (d.status === 'failed' || d.status === 'denied');
}

export const api = {
  reply: (input: ReplyInput) =>
    authedFetch<ReplyResult>('/agent/message', { method: 'POST', body: JSON.stringify(input) }),
  setStar: (tenantCode: string, chatId: string, agentUid: string, starred: boolean) =>
    authedFetch<{ ok: true; starred: boolean }>('/agent/chat/star', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, chatId, agentUid, starred }),
    }),
  setStatus: (tenantCode: string, chatId: string, agentUid: string, status: 'open' | 'pending' | 'snoozed' | 'resolved') =>
    authedFetch<{ ok: true }>('/agent/status', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, chatId, agentUid, status }),
    }),
  assign: (tenantCode: string, chatId: string, agentUid: string, assigneeUid: string) =>
    authedFetch<{ ok: true }>('/agent/assign', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, chatId, agentUid, assigneeUid }),
    }),
  setPriority: (tenantCode: string, chatId: string, agentUid: string, priority: 'low' | 'medium' | 'high' | 'urgent') =>
    authedFetch<{ ok: true }>('/agent/priority', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, chatId, agentUid, priority }),
    }),
  snooze: (tenantCode: string, chatId: string, agentUid: string, untilMs: number) =>
    authedFetch<{ ok: true }>('/agent/snooze', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, chatId, agentUid, untilMs }),
    }),
  // Park a conversation on someone else: 'customer' (the ball is with them) or
  // 'internal' (blocked on another team). Server-side this is a snooze with a
  // far-future wake plus a waitReason, so it stays out of the live queues
  // without ever auto-resolving. unpark puts it straight back to open.
  park: (tenantCode: string, chatId: string, reason: 'customer' | 'internal') =>
    authedFetch<{ ok: true }>(`/agent/chat/${encodeURIComponent(chatId)}/park`, {
      method: 'POST',
      body: JSON.stringify({ tenantCode, reason }),
    }),
  unpark: (tenantCode: string, chatId: string) =>
    authedFetch<{ ok: true }>(`/agent/chat/${encodeURIComponent(chatId)}/unpark`, {
      method: 'POST',
      body: JSON.stringify({ tenantCode }),
    }),
  setLabels: (tenantCode: string, chatId: string, agentUid: string, add?: string[], remove?: string[]) =>
    authedFetch<{ ok: true }>('/agent/labels', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, chatId, agentUid, add, remove }),
    }),
  autoAssign: (tenantCode: string, chatId: string, agentUid: string, inboxId: string) =>
    authedFetch<{ ok: true; assigneeUid: string }>('/agent/auto-assign', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, chatId, agentUid, inboxId }),
    }),
  assignToTeam: (tenantCode: string, agentUid: string, chatId: string, teamId: string | '') =>
    authedFetch<{ ok: true }>('/agent/assign-team', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId, teamId: teamId || null }),
    }),
  presence: (tenantCode: string, agentUid: string, status: 'online' | 'busy' | 'offline', typingIn?: string) =>
    authedFetch<{ ok: true }>('/agent/presence', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, status, typingIn }),
    }),

  polish: (text: string, locale?: string) =>
    authedFetch<{ text: string; changed: boolean }>('/agent/polish', {
      method: 'POST',
      body: JSON.stringify({ text, locale }),
    }),

  // Admin CRUD (generic)
  list: <T>(resource: string) => authedFetch<{ items: T[] }>(`/admin/${resource}`),
  create: <T = { id: string }>(resource: string, body: Record<string, unknown>) =>
    authedFetch<T>(`/admin/${resource}`, { method: 'POST', body: JSON.stringify(body) }),
  patch: (resource: string, id: string, body: Record<string, unknown>) =>
    authedFetch<{ ok: true }>(`/admin/${resource}/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (resource: string, id: string) =>
    authedFetch<{ ok: true }>(`/admin/${resource}/${id}`, { method: 'DELETE' }),

  rotateBot: (id: string) => authedFetch<{ token: string }>(`/admin/bots/${id}/rotate`, { method: 'POST' }),

  listAuditLogs: (limit = 100, after?: number) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (after) params.set('after', String(after));
    return authedFetch<{ items: Array<Record<string, unknown>>; nextCursor: number | null }>(
      `/admin/audit-logs?${params.toString()}`
    );
  },

  bulkUpdateChats: (
    tenantCode: string,
    agentUid: string,
    chatIds: string[],
    op:
      | { action: 'status' | 'label_add' | 'label_remove' | 'assign' | 'snooze'; value: unknown }
      | { action: 'park'; value: 'customer' | 'internal' }
  ) =>
    authedFetch<{ updated: number }>('/agent/bulk', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatIds, op }),
    }),

  markRead: (tenantCode: string, agentUid: string, chatId: string) =>
    authedFetch<{ ok: true }>('/agent/mark-read', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId }),
    }),
  markUnread: (tenantCode: string, agentUid: string, chatId: string) =>
    authedFetch<{ ok: true }>('/agent/mark-unread', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId }),
    }),

  // Ask the AI for a DIFFERENT suggested reply, optionally steered by guidance
  // ("make it shorter", "they're in Israel not abroad"). Re-runs the brain in
  // suggest mode and overwrites chat.suggestedReply (does NOT send to the
  // customer); the new draft arrives via the live chat-doc subscription.
  regenerate: (tenantCode: string, chatId: string, guidance?: string) =>
    authedFetch<{ ok: true; suggestedReply?: { content: string } }>('/agent/regenerate', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, chatId, guidance: guidance?.trim() || undefined }),
    }),

  addParticipant: (tenantCode: string, agentUid: string, chatId: string, participantUid: string) =>
    authedFetch<{ ok: true }>('/agent/participants', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId, add: participantUid }),
    }),

  runMacro: (tenantCode: string, agentUid: string, chatId: string, macroId: string) =>
    authedFetch<{ ok: true }>('/agent/macro', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId, macroId }),
    }),

  setCustomAttr: (tenantCode: string, agentUid: string, chatId: string, key: string, value: unknown) =>
    authedFetch<{ ok: true }>('/agent/custom-attr', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId, key, value }),
    }),

  transcriptUrl: (tenantCode: string, chatId: string, format: 'text' | 'html' = 'text') =>
    `${apiBase}/agent/transcript?tenantCode=${encodeURIComponent(tenantCode)}&chatId=${encodeURIComponent(chatId)}&format=${format}`,

  conversationsExportUrl: (tenantCode: string, opts: { fromMs?: number; toMs?: number; status?: string; limit?: number } = {}) => {
    const p = new URLSearchParams({ tenantCode });
    if (opts.fromMs) p.set('fromMs', String(opts.fromMs));
    if (opts.toMs) p.set('toMs', String(opts.toMs));
    if (opts.status) p.set('status', opts.status);
    if (opts.limit) p.set('limit', String(opts.limit));
    return `${apiBase}/agent/conversations-export?${p.toString()}`;
  },

  editMessage: (tenantCode: string, agentUid: string, chatId: string, messageId: string, content: string) =>
    authedFetch<{ ok: true }>('/agent/message/edit', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId, messageId, content }),
    }),

  getSubscriber: (tenantCode: string, contactId: string, excludeChatId?: string) => {
    const params = new URLSearchParams({ tenantCode, contactId });
    if (excludeChatId) params.set('excludeChatId', excludeChatId);
    return authedFetch<Subscriber360>(`/agent/subscriber?${params.toString()}`);
  },

  suspendLine: (tenantCode: string, agentUid: string, contactId: string, lineId: string, reason: string, reasonDetail?: string) =>
    authedFetch<{ ok: boolean; data?: unknown; error?: string }>('/agent/customer/suspend-line', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId, lineId, reason, reasonDetail }),
    }),

  restoreLine: (tenantCode: string, agentUid: string, contactId: string, lineId: string) =>
    authedFetch<{ ok: boolean; data?: unknown; error?: string }>('/agent/customer/restore-line', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId, lineId }),
    }),

  createTicket: (
    tenantCode: string,
    agentUid: string,
    contactId: string,
    input: { category: string; subject: string; description: string; subscriberId?: string; priority?: string }
  ) =>
    authedFetch<{ ok: boolean; data?: unknown; error?: string; ticketId?: string }>('/agent/customer/create-ticket', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId, ...input }),
    }),

  // ── Tracked tickets ('tickets' collection in the tenant DB) ───────────────
  // Distinct from createTicket above, which only fires an email at the support
  // inbox and leaves nothing behind to track. These three back the Tickets page:
  // source 'internal' is the team's own issue list, 'customer' is a ticket
  // raised for a subscriber. The caller is identified by the bearer token, so
  // no agentUid travels in the body.
  raiseTicket: (
    tenantCode: string,
    input: {
      subject: string;
      description: string;
      category: string;
      priority: TicketPriority;
      /** The POST body is a zod enum of exactly customer|internal server-side —
       *  the wider row vocabulary ('ai', 'mobile-app', …) is read-only. */
      source: WritableTicketSource;
      /** Free text, only meaningful for internal issues. */
      area?: string;
      contactId?: string;
      chatId?: string;
      customerEmail?: string;
    }
  ) =>
    authedFetch<{ ok: true; ticketId: string; ticketNumber: string }>('/agent/tickets', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, ...input }),
    }),

  listTickets: (
    tenantCode: string,
    opts: {
      status?: TicketStatus;
      /** Server-side equality filter on the raw doc value. The Tickets page
       *  deliberately does NOT send it — it buckets client-side so a source it
       *  has never heard of still appears somewhere. */
      source?: TicketSource;
      assigneeUid?: string;
      /** Free-text match over ticket number / subject / customer. */
      q?: string;
      limit?: number;
    } = {}
  ) => {
    const p = new URLSearchParams({ tenantCode });
    for (const [k, v] of Object.entries(opts)) {
      if (v !== undefined && v !== '') p.set(k, String(v));
    }
    return authedFetch<{ tickets: Ticket[] }>(`/agent/tickets?${p.toString()}`);
  },

  // Every field is optional: the list page PATCHes one control at a time, and
  // `comment` appends a note without touching anything else.
  updateTicket: (
    tenantCode: string,
    ticketId: string,
    patch: {
      status?: TicketStatus;
      assigneeUid?: string;
      priority?: TicketPriority;
      comment?: string;
    }
  ) =>
    authedFetch<{ ok: true }>(`/agent/tickets/${encodeURIComponent(ticketId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ tenantCode, ...patch }),
    }),

  toggleRoaming: (tenantCode: string, agentUid: string, contactId: string, lineId: string, enabled: boolean, dataRoaming?: boolean) =>
    authedFetch<ActionResult>('/agent/customer/toggle-roaming', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId, lineId, enabled, dataRoaming }),
    }),

  changePlan: (
    tenantCode: string,
    agentUid: string,
    contactId: string,
    subscriberId: string,
    newPlanCode: string,
    previewOnly = true,
    effectiveDate?: string
  ) =>
    authedFetch<ActionResult>('/agent/customer/change-plan', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId, subscriberId, newPlanCode, previewOnly, effectiveDate }),
    }),

  setCallForwarding: (
    tenantCode: string,
    agentUid: string,
    contactId: string,
    lineId: string,
    forwardType: 'unconditional' | 'busy' | 'no_answer' | 'unreachable',
    destination: string,
    enabled = true
  ) =>
    authedFetch<ActionResult>('/agent/customer/set-call-forwarding', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId, lineId, forwardType, destination, enabled }),
    }),

  setRingCount: (tenantCode: string, agentUid: string, contactId: string, lineId: string, ringCount: number) =>
    authedFetch<ActionResult>('/agent/customer/set-ring-count', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId, lineId, ringCount }),
    }),

  configureVoicemail: (
    tenantCode: string,
    agentUid: string,
    contactId: string,
    lineId: string,
    settings: {
      enabled?: boolean;
      ringsBeforeVoicemail?: number;
      greeting?: 'default' | 'custom' | 'name_only';
      transcription?: boolean;
      notifyBySms?: boolean;
    }
  ) =>
    authedFetch<ActionResult>('/agent/customer/configure-voicemail', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId, lineId, ...settings }),
    }),

  simSwap: (
    tenantCode: string,
    agentUid: string,
    contactId: string,
    lineId: string,
    newIccid: string,
    reason: 'damaged' | 'lost' | 'upgrade' | 'device_change' | 'other'
  ) =>
    authedFetch<ActionResult>('/agent/customer/sim-swap', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId, lineId, newIccid, reason }),
    }),

  toggleAi: (tenantCode: string, agentUid: string, chatId: string, take: boolean) =>
    authedFetch<{ ok: boolean; aiHandled: boolean }>('/agent/chat/ai-toggle', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId, take }),
    }),

  // Agent-assisted card verification — enter the customer's email/phone +
  // last-4; the real WooCommerce card check runs server-side. (Replaces the
  // old "vouch" manual-verify: agents can no longer self-attest identity.)
  verifyCard: (
    tenantCode: string,
    chatId: string,
    last4: string,
    id: { email?: string; phone?: string }
  ) =>
    authedFetch<{
      configured?: boolean;
      verified?: boolean;
      reason?: string;
      rate_limited?: boolean;
    }>('/agent/wp/verify', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, chatId, last4, email: id.email, phone: id.phone }),
    }),

  searchChats: (tenantCode: string, q: string, type?: 'user' | 'phone' | 'content') => {
    const params = new URLSearchParams({ tenantCode, q });
    if (type) params.set('type', type);
    return authedFetch<{ items: SearchHit[] }>(`/agent/search?${params.toString()}`);
  },

  listFaqs: (tenantCode: string, opts: { category?: string; q?: string } = {}) => {
    const params = new URLSearchParams({ tenantCode });
    if (opts.category) params.set('category', opts.category);
    if (opts.q) params.set('q', opts.q);
    return authedFetch<{ items: Faq[]; categories: Array<{ slug: string; label: string; count: number }> }>(
      `/agent/faqs?${params.toString()}`
    );
  },

  listWaTemplates: (tenantCode: string, inboxId: string) =>
    authedFetch<{ items: Array<{ name: string; language: string; status: string; category: string }> }>(
      `/agent/wa-templates?tenantCode=${encodeURIComponent(tenantCode)}&inboxId=${encodeURIComponent(inboxId)}`
    ),

  uploadInit: (tenantCode: string, chatId: string, file: File) =>
    authedFetch<{ uploadUrl: string; attachmentId: string; storagePath: string; expiresAt: number }>(
      '/agent/upload',
      {
        method: 'POST',
        body: JSON.stringify({
          tenantCode,
          chatId,
          filename: file.name,
          mimeType: file.type,
          size: file.size,
        }),
      }
    ),

  // Reactions: add or remove the agent's uid from a message's emoji reaction array.
  reactToMessage: (
    tenantCode: string,
    agentUid: string,
    chatId: string,
    messageId: string,
    emoji: string,
    op: 'add' | 'remove',
  ) =>
    authedFetch<{ ok: true }>('/agent/message/react', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId, messageId, emoji, op }),
    }),

  // One-click agent feedback on an AI answer. Tolerates 404 so an older
  // backend never breaks the UI.
  rateAiMessage: (
    tenantCode: string,
    agentUid: string,
    chatId: string,
    input: { messageId?: string; aiDecisionId?: string; verdict: 'good' | 'bad'; reason?: string },
  ) =>
    authedFetch<{ ok: true }>('/agent/feedback', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId, ...input }),
    }).catch(() => ({ ok: true as const })),

  // Clear the AI-Flagged marker on a chat ("mark reviewed") — removes it from
  // the AI Flagged worklist without touching the 👎 feedback history (F11).
  flagReviewed: (tenantCode: string, agentUid: string, chatId: string) =>
    authedFetch<{ ok: true }>('/agent/flag/reviewed', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId }),
    }),

  // Save the resolved case to the bot's support memory (so it can reuse the
  // human resolution on similar future questions).
  saveToMemory: (
    tenantCode: string,
    agentUid: string,
    chatId: string,
    input?: { resolution?: string; approved?: boolean; messageId?: string },
  ) =>
    authedFetch<{ ok: boolean; id?: string; error?: string }>('/agent/memory/save', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId, ...(input ?? {}) }),
    }),

  // Teach a behavioural RULE from a bad AI answer ("he is already verified,
  // stop asking for his card digits"). Routed to the flows/playbook path, NOT
  // support memory: support memory is replayed to customers verbatim as a
  // verified answer, so a rule written there poisons it. Always lands as a
  // DRAFT that has to be approved in Settings -> Support flows.
  saveFlowFromCorrection: (
    tenantCode: string,
    agentUid: string,
    chatId: string,
    input: { messageId: string; rule: string; title?: string; intents?: string[]; keywords?: string[] },
  ) =>
    authedFetch<AiFlow>('/agent/flows/from-correction', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId, ...input }),
    }),

  // Revert a saved answer (remove it from the bot's support memory). messageId
  // scopes the delete to THAT correction; omit it for a whole-chat revert.
  deleteFromMemory: (tenantCode: string, agentUid: string, chatId: string, messageId?: string) =>
    authedFetch<{ ok: boolean; error?: string }>('/agent/memory/delete', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId, ...(messageId ? { messageId } : {}) }),
    }),

  // Typing indicator: agent -> customer. Backend tolerates 404 (route may not exist yet).
  setTyping: (tenantCode: string, agentUid: string, chatId: string, typing: boolean) =>
    authedFetch<{ ok: true }>('/agent/typing', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId, typing }),
    }).catch(() => ({ ok: true as const })),

  // Agent-initiated outbound: POST /agent/proactive. Resolves/creates the
  // contact + chat, then dispatches through the consent + outbound-policy gate.
  // On policy/consent denial the backend returns status 'denied'/'failed' with a
  // reason (HTTP 409/200); on success status 'sent' (HTTP 201) with chatId.
  startConversation: (input: StartConversationInput) =>
    authedFetch<ProactiveResult>('/agent/proactive', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // ── Multi-tenant shell ────────────────────────────────────────────────────
  // Tenants this agent may access, derived server-side from the auth claim's
  // tenantCodes (∪ legacy tenantCode) with branding from the platform
  // 'tenants/{code}' doc.
  myTenants: () => authedFetch<{ items: TenantInfo[] }>('/agent/my-tenants'),

  // Register this device's push token in the active tenant's DB.
  registerDevice: (tenantCode: string, token: string, platform: string) =>
    authedFetch<{ ok: true }>('/agent/device', {
      method: 'POST',
      body: JSON.stringify({ token, platform, tenantCode }),
    }),

  // Best-effort removal of the device's push token.
  unregisterDevice: (token: string) =>
    authedFetch<{ ok: true }>('/agent/device', {
      method: 'DELETE',
      body: JSON.stringify({ token }),
    }),
};

/** Tenant the agent may operate in, with optional branding. */
export interface TenantInfo {
  code: string;
  name: string;
  logoUrl?: string;
  primaryColor?: string;
}

// ── Agent-initiated outbound (New conversation) ─────────────────────────────

export type ProactiveChannel = 'whatsapp' | 'email' | 'sms' | 'facebook';

export interface StartConversationInput {
  tenantCode: string;
  agentUid: string;
  inboxId: string;
  channel: ProactiveChannel;
  to: { email?: string; phone?: string; psid?: string };
  intent: {
    category: 'service_reply' | 'utility' | 'marketing' | 'authentication' | 'transactional';
    initiatedBy?: 'agent' | 'ai' | 'system_proactive';
    templateCategory?: string;
  };
  content: string;
  subject?: string;
  template?: { name: string; language: string; parameters?: string[] };
  /** Optional display name; persisted onto a newly-created contact. */
  displayName?: string;
  idempotencyKey: string;
}

export interface ProactiveResult {
  status: 'sent' | 'denied' | 'failed';
  chatId?: string;
  channel?: string;
  reason?: string;
  channelMessageId?: string;
  idempotent?: boolean;
}

// ── Saved views (server-persisted, per-agent) ───────────────────────────────
// Backed by the `custom-views` admin CRUD (collection `custom_views`, agent
// scope). Each doc carries { name, filters (the serialized view), owner }.

export interface CustomViewDoc {
  id: string;
  name: string;
  filters: Record<string, unknown>;
  owner: string;
}

export const viewsApi = {
  list: () => authedFetch<{ items: CustomViewDoc[] }>('/admin/custom-views'),
  create: (body: { name: string; filters: Record<string, unknown>; owner: string }) =>
    authedFetch<{ id: string }>('/admin/custom-views', { method: 'POST', body: JSON.stringify(body) }),
  remove: (id: string) =>
    authedFetch<{ ok: true }>(`/admin/custom-views/${id}`, { method: 'DELETE' }),
};

// Find a contact's most-recent conversation. Queries `chats` where
// contactId == X ordered by lastMessageAt desc, limit 1 (covered by the
// (contactId, inboxId, status, lastMessageAt) composite index; the simpler
// contactId+lastMessageAt ordering is single-field-indexable). Returns the
// chatId or null when the contact has no conversation yet.
export async function getLatestChatForContact(contactId: string): Promise<string | null> {
  // Equality-only query (single-field auto-index) then pick latest client-side,
  // so we don't depend on a composite (contactId, lastMessageAt) index.
  const q = query(collection(db, 'chats'), where('contactId', '==', contactId), fsLimit(50));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const ts = (d: (typeof snap.docs)[number]) =>
    (d.data().lastMessageAt ?? d.data().createdAt ?? 0) as number;
  return snap.docs.reduce((best, d) => (ts(d) > ts(best) ? d : best), snap.docs[0]).id;
}

export type ActionResult = { ok: boolean; data?: unknown; error?: string };

export interface Subscriber360 {
  identity: {
    contactId: string;
    name?: string;
    email?: string;
    phone?: string;
    subscriberId?: string;
    customerId?: string;
    tenantCode: string;
  };
  subscriber?: {
    status?: string;
    msisdn?: string;
    activatedAt?: number;
    planName?: string;
    customer?: { firstName?: string; lastName?: string; email?: string; phone?: string };
    lines?: Array<{ id: string; phoneNumber?: string; status?: string; planName?: string; simType?: string }>;
  };
  billing?: {
    currentBalance?: number;
    balance?: number;
    nextBillingDate?: number;
    currentInvoice?: { amount?: number; total?: number; status?: string };
    paymentMethod?: { type?: string; lastFour?: string };
    autopayEnabled?: boolean;
    recentPayments?: Array<{ amount: number; date: number }>;
  };
  usage?: Array<{
    lineId: string;
    phoneNumber?: string;
    data?: { used: number; limit?: number; unlimited?: boolean };
    voice?: { used: number; limit?: number; unlimited?: boolean };
    sms?: { used: number; limit?: number; unlimited?: boolean };
  }>;
  brandAdminUrl?: string;
  verified?: boolean;
  /** True when this tenant is WP-backed (Bekesher); hides native 360/lines. */
  wpConfigured?: boolean;
  pastChats?: Array<{
    id: string;
    status: string;
    channel: string;
    createdAt: number;
    lastMessageAt: number;
    lastMessagePreview: string;
    assigneeUid: string | null;
  }>;
  lineDetails?: Array<{
    lineId: string;
    phoneNumber?: string;
    network?: {
      registered?: boolean;
      technology?: string;
      roaming?: boolean;
      roamingCountry?: string;
      roamingNetwork?: string;
      signalStrength?: number;
      signalQuality?: string;
      lastSeen?: string;
    };
    porting?: {
      portingId?: string;
      status?: string;
      donorCarrier?: string;
      submittedAt?: string;
      expectedCompletion?: string;
    } | null;
    forwarding?: {
      rules?: Array<{ type: string; destination: string; enabled: boolean; ringTimeout?: number }>;
    };
  }>;
  availablePlans?: Array<{
    planCode?: string;
    code?: string;
    name?: string;
    monthlyPrice?: number;
    price?: number;
    currency?: string;
    dataLimit?: string | number;
    description?: string;
  }>;
}

export interface Faq {
  id: string;
  question: string;
  answer: string;
  category: string;
  categoryLabel?: string;
  tags?: string[];
  priority?: number;
  published?: boolean;
  locale?: string;
}

export interface SearchHit {
  type: 'contact' | 'chat';
  chatId?: string;
  contactId: string;
  name?: string;
  email?: string;
  phone?: string;
  preview?: string;
  lastMessageAt?: number;
}

export interface ReportRange {
  fromMs?: number;
  toMs?: number;
}

export async function fetchReport<T>(
  name: 'conversations' | 'agents' | 'csat' | 'ai-quality',
  range: ReportRange,
): Promise<T> {
  const params = new URLSearchParams();
  if (range.fromMs) params.set('fromMs', String(range.fromMs));
  if (range.toMs) params.set('toMs', String(range.toMs));
  return authedFetch<T>(`/reports/${name}?${params.toString()}`);
}

export interface AiQualitySummary {
  turns: number;
  delivered: number;
  suggested: number;
  escalated: number;
  unfaithful: number;
  badFeedback: number;
  goodFeedback: number;
  successRate: number;
  escalationRate: number;
  byIntent: Array<{ intent: string; count: number }>;
  truncated: boolean;
  /** Server could not run the scan (missing/building index) — the zeros are "unknown", not "none". */
  degraded?: boolean;
}

export async function uploadFile(tenantCode: string, chatId: string, file: File): Promise<string> {
  const init = await api.uploadInit(tenantCode, chatId, file);
  const res = await fetch(init.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  // Label only — the signed URL carries a credential, so it never gets logged.
  if (!res.ok) throw await toApiError(res, 'storage upload');
  return init.storagePath;
}

// ============================================================================
// Inbox / SubscriberPanel extensions (Agent 3)
// ============================================================================

export interface ContactNote {
  id: string;
  note: string;
  authorUid: string;
  authorEmail?: string;
  createdAt: number;
}

export interface WpLineBalance {
  configured: boolean;
  gated?: string;
  reason?: string;
  ok?: boolean;
  line_id?: number;
  phone?: string;
  balance?: number | string;
  prepaid_data_usage?: Array<{
    package?: string;
    name?: string;
    used?: number | string;
    remaining?: number | string;
    total?: number | string;
    expiration?: string;
  }>;
}

export const inboxApi = {
  getContactNotes: (tenantCode: string, contactId: string) =>
    authedFetch<{ items: ContactNote[] }>(
      `/agent/contact/notes?tenantCode=${encodeURIComponent(tenantCode)}&contactId=${encodeURIComponent(contactId)}`
    ),
  addContactNote: (tenantCode: string, agentUid: string, contactId: string, note: string) =>
    authedFetch<{ ok: true; id?: string }>('/agent/contact/notes', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId, note }),
    }),
  getAiQuota: (tenantCode: string, chatId: string) =>
    authedFetch<{
      day: string;
      identified: boolean;
      keyKind: 'email' | 'phone' | 'ip' | null;
      count: number;
      limit: number;
      channel: string | null;
      deviceInfo: { ip?: string; userAgent?: string; language?: string } | null;
    }>(
      `/agent/ai-quota?tenantCode=${encodeURIComponent(tenantCode)}&chatId=${encodeURIComponent(chatId)}`
    ),
  ipLookup: (ip: string) =>
    authedFetch<{
      status?: string;
      country?: string;
      countryCode?: string;
      regionName?: string;
      city?: string;
      zip?: string;
      lat?: number;
      lon?: number;
      timezone?: string;
      isp?: string;
      org?: string;
      as?: string;
      query?: string;
      message?: string;
    }>(`/agent/ip-lookup?ip=${encodeURIComponent(ip)}`),
  blockContact: (tenantCode: string, agentUid: string, contactId: string, reason?: string) =>
    authedFetch<{ ok: true }>('/agent/contact/block', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId, reason }),
    }),
  unblockContact: (tenantCode: string, agentUid: string, contactId: string) =>
    authedFetch<{ ok: true }>('/agent/contact/unblock', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId }),
    }),
  // IP/device block — for anonymous visitors (contactId-block is ineffective
  // because anon ids are session-scoped). Server resolves the IP from the chat.
  blockIp: (tenantCode: string, agentUid: string, chatId: string, reason?: string) =>
    authedFetch<{ ok: true }>('/agent/ip/block', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId, reason }),
    }),
  unblockIp: (tenantCode: string, agentUid: string, chatId: string) =>
    authedFetch<{ ok: true }>('/agent/ip/unblock', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatId }),
    }),
  // WP bridge (read-only). config = cheap "is this tenant WP-backed" probe.
  wpConfig: (tenantCode: string) =>
    authedFetch<{ configured: boolean }>(`/agent/wp/config?tenantCode=${encodeURIComponent(tenantCode)}`),
  wpCustomer: (tenantCode: string, chatId: string) =>
    authedFetch<{
      configured: boolean;
      found?: boolean;
      display_name?: string;
      first_name?: string;
      last_name?: string;
      billing_phone?: string;
      email?: string;
    }>(`/agent/wp/customer?tenantCode=${encodeURIComponent(tenantCode)}&chatId=${encodeURIComponent(chatId)}`),
  wpLineBalance: (tenantCode: string, chatId: string, lineId: string | number) =>
    authedFetch<WpLineBalance>(
      `/agent/wp/line-balance?tenantCode=${encodeURIComponent(tenantCode)}&chatId=${encodeURIComponent(chatId)}&lineId=${encodeURIComponent(String(lineId))}`
    ),
  exportContactData: async (tenantCode: string, agentUid: string, contactId: string) => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    const token = await user.getIdToken();
    const res = await fetch(`${apiBase}/agent/contact/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...tenantHeaders(),
      },
      body: JSON.stringify({ tenantCode, agentUid, contactId }),
    });
    if (!res.ok) throw await toApiError(res, '/agent/contact/export');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contact-${contactId}.ndjson`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  deleteContactData: (tenantCode: string, agentUid: string, contactId: string, reason: string) =>
    authedFetch<{ ok: true }>('/agent/contact/delete', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId, reason }),
    }),
  mergeChats: (tenantCode: string, agentUid: string, primaryChatId: string, secondaryChatId: string) =>
    authedFetch<{ ok: true }>('/agent/chat/merge', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, primaryChatId, secondaryChatId }),
    }),
  bulkDeleteChats: (tenantCode: string, agentUid: string, chatIds: string[]) =>
    authedFetch<{ updated: number }>('/agent/bulk', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, chatIds, op: { action: 'delete', value: null } }),
    }),
  // Best-effort view audit; backend route may not exist yet.
  auditSubscriberView: (tenantCode: string, agentUid: string, contactId: string) =>
    authedFetch<{ ok: true }>('/agent/audit/subscriber-view', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId }),
    }),
};

// ============================================================================
// Settings + Reports extensions (working hours, SLA, assignment, automations,
// webhook deliveries, security config, CSAT comments, intent/resolution
// breakdowns, onboarding bootstrap). All methods below are additive and
// tolerate 404 from the backend (returns { unsupported: true }) so the UI can
// render a graceful fallback.
// ============================================================================

export interface WebhookDelivery {
  id: string;
  webhookId: string;
  url: string;
  event: string;
  status: 'success' | 'failed' | 'pending';
  httpCode?: number;
  durationMs?: number;
  attemptedAt: number;
  errorMessage?: string;
}

export interface SecurityConfig {
  require2FA?: boolean;
  idleTimeoutMin?: number;
}

export interface CsatComment {
  id: string;
  chatId: string;
  score?: number;
  comment: string;
  createdAt: number;
}

export type Unsupported = { unsupported: true };

function isUnsupported(v: unknown): v is Unsupported {
  return typeof v === 'object' && v !== null && (v as Unsupported).unsupported === true;
}
export { isUnsupported };

async function tolerantFetch<T>(promise: Promise<T>): Promise<T | Unsupported> {
  try {
    return await promise;
  } catch (err) {
    // 404 here means "this backend doesn't have the route yet" and the caller
    // renders a fallback. Keyed on the typed status — it used to regex a 404 out
    // of the stringified error body, which stopped containing one the moment
    // response bodies stopped being spliced into Error.message.
    if (err instanceof ApiError && err.status === 404) return { unsupported: true };
    throw err;
  }
}

export const apiExt = {
  listWebhookDeliveries: (limit = 50, after?: string) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (after) params.set('after', after);
    return tolerantFetch(
      authedFetch<{ items: WebhookDelivery[]; nextCursor: string | null }>(
        `/admin/webhook-deliveries?${params.toString()}`
      )
    );
  },

  getSecurityConfig: () =>
    tolerantFetch(authedFetch<SecurityConfig>('/admin/config/security')),

  setSecurityConfig: (body: SecurityConfig) =>
    tolerantFetch(
      authedFetch<{ ok: true }>('/admin/config/security', {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
    ),

  listCsatComments: (fromMs: number, toMs: number, limit = 20) => {
    const params = new URLSearchParams({
      fromMs: String(fromMs),
      toMs: String(toMs),
      limit: String(limit),
    });
    return tolerantFetch(
      authedFetch<{ items: CsatComment[] }>(`/reports/csat-comments?${params.toString()}`)
    );
  },

  bootstrapTenant: (body: { tenantCode: string; tenantName: string; adminEmail: string; inboxName: string }) =>
    tolerantFetch(
      authedFetch<{ ok: true; tenantCode: string; inboxId: string }>('/admin/bootstrap', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    ),

  seedDefaults: (tenantCode: string) =>
    tolerantFetch(
      authedFetch<{ ok: true }>('/admin/seed-defaults', {
        method: 'POST',
        body: JSON.stringify({ tenantCode }),
      })
    ),
};

export async function fetchResolutionTimes(range: ReportRange): Promise<number[]> {
  const params = new URLSearchParams({ withResolutionMs: '1' });
  if (range.fromMs) params.set('fromMs', String(range.fromMs));
  if (range.toMs) params.set('toMs', String(range.toMs));
  try {
    const r = await authedFetch<{ resolutionMs?: number[] }>(
      `/reports/conversations?${params.toString()}`
    );
    return r.resolutionMs || [];
  } catch {
    return [];
  }
}

export async function fetchIntentBreakdown(range: ReportRange): Promise<Record<string, number>> {
  const params = new URLSearchParams();
  if (range.fromMs) params.set('fromMs', String(range.fromMs));
  if (range.toMs) params.set('toMs', String(range.toMs));
  try {
    const r = await authedFetch<{ byIntent?: Record<string, number> }>(
      `/reports/intents?${params.toString()}`
    );
    return r.byIntent || {};
  } catch {
    return {};
  }
}

// ── Directory + Contacts + audit helpers ────────────────────────────────────

export interface DirectoryAgent {
  id: string;
  uid: string;
  displayName: string;
  email?: string;
  role?: string;
}

export interface ContactRow {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  identifiers?: { email?: string; phone?: string; psid?: string };
  subscriberId?: string;
  customerId?: string;
  verified?: boolean;
  blocked?: boolean;
  /**
   * Persisted anonymity marker: true = a throwaway widget visitor (`anon-<ts>`
   * contactId, no email/phone, one per session and never reused). Hidden from
   * the directory by default — see `ContactsPage.anonymity`.
   */
  anonymous?: boolean;
  createdAt?: number;
  updatedAt?: number;
  lastSeenAt?: number;
}

// Internal Olami staff contacts (email or identifiers.email on @olami.net /
// @olami.com). Hidden from the Contacts list for everyone except super_admin.
const OLAMI_EMAIL_RE = /@olami\.(net|com)$/i;
export function isOlamiContact(c: Pick<ContactRow, 'email' | 'identifiers'>): boolean {
  return OLAMI_EMAIL_RE.test(c.email ?? '') || OLAMI_EMAIL_RE.test(c.identifiers?.email ?? '');
}

export interface AiFlow {
  id: string;
  title: string;
  trigger: { intents?: string[]; keywords?: string[] };
  steps: { text: string }[];
  status: 'draft' | 'approved';
  updatedAt?: number;
  updatedBy?: string;
  /** Server's verdict on whether this draft can EVER fire — false means it has
   *  no intent and no keyword, so it matches no customer turn and approving it
   *  is a no-op. Written by /flows/from-correction; absent on older docs and on
   *  hand-authored flows, which is NOT the same as false (see savedRuleToast). */
  triggerDerived?: boolean;
  // Provenance — present only on GENERATED drafts, so every field is optional
  // and must be rendered defensively. 'correction' = an agent taught a rule from
  // the inbox popover; 'feedback' = the nightly ai_feedback digest proposed one
  // from repeated downvotes (it also fills rationale + evidence).
  source?: 'correction' | 'feedback' | 'manual';
  sourceChatId?: string;
  sourceMessageId?: string;
  sourceQuestion?: string;
  sourceAnswer?: string;
  rationale?: string;
  evidence?: { chatId?: string; reason?: string }[];
  createdAt?: number;
  // Server-derived author of a generated draft.
  createdBy?: string;
  // True when the server stripped the quoted conversation from THIS response:
  // `ai_flows` is tenant-wide, so the customer/AI text a draft was written about
  // goes only to admins (who review) and to the draft's author. Everyone else
  // gets the rule without the quote.
  sourceRedacted?: boolean;
}

export interface AgentAccess {
  uid: string;
  email: string | null;
  displayName: string;
  role: string;
  inboxIds: string[];
}

export const agentAccessApi = {
  list: () => authedFetch<{ items: AgentAccess[] }>('/admin/agent-access').catch(() => ({ items: [] as AgentAccess[] })),
  set: (agentUid: string, inboxIds: string[]) =>
    authedFetch<{ ok: true; inboxIds: string[] }>('/admin/agent-access', {
      method: 'POST',
      body: JSON.stringify({ agentUid, inboxIds }),
    }),
};

export const flowsApi = {
  list: (tenantCode: string) =>
    authedFetch<{ items: AiFlow[] }>(`/agent/flows?tenantCode=${encodeURIComponent(tenantCode)}`)
      .catch(() => ({ items: [] as AiFlow[] })),
  save: (tenantCode: string, agentUid: string, flow: Omit<AiFlow, 'updatedAt' | 'updatedBy'>) =>
    authedFetch<AiFlow>('/agent/flows', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, ...flow }),
    }),
  approve: (tenantCode: string, id: string, approve: boolean) =>
    authedFetch<{ ok: true; status: string }>(`/agent/flows/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: JSON.stringify({ tenantCode, approve }),
    }),
  remove: (tenantCode: string, id: string) =>
    authedFetch<{ ok: true }>(`/agent/flows/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: JSON.stringify({ tenantCode }),
    }),
};

// ── Known issues (F-4) ──────────────────────────────────────────────────────
// The operator's "we already know" notice: the bot stops troubleshooting an
// incident support has declared, and the console shows it above the queue.
// Reads are open to every agent; declaring/editing/resolving is admin-only and
// audited server-side (routes/agent.ts).

/** The writable half of a known issue. `startsAt` defaults to now server-side. */
export interface KnownIssueInput {
  title: string;
  message: string;
  titleHe?: string;
  messageHe?: string;
  active?: boolean;
  startsAt?: number;
  /** null = open-ended. Must be later than startsAt or the server 400s. */
  endsAt?: number | null;
}

/**
 * Is this notice live at `now`? Same three-part rule as the server's
 * services/known-issues.ts, deliberately duplicated rather than taken from the
 * row's `isActive`: that flag was decided when the list was fetched, and this
 * console sits open all day. A notice whose `endsAt` has since passed has to
 * disappear on its own, not on the next reload.
 */
export function isKnownIssueActive(
  issue: Pick<KnownIssue, 'active' | 'startsAt' | 'endsAt'>,
  now: number = Date.now(),
): boolean {
  if (issue.active !== true) return false;
  const startsAt = Number(issue.startsAt);
  // Unreadable start time ⇒ not started: a broken doc must not be able to put
  // words in the bot's mouth (or on the agent's screen).
  if (!Number.isFinite(startsAt) || startsAt > now) return false;
  const endsAt = issue.endsAt;
  if (endsAt === undefined || endsAt === null) return true; // open-ended → resolve ends it
  const end = Number(endsAt);
  if (!Number.isFinite(end)) return true; // unreadable end ⇒ treat as open-ended
  return end > now;
}

/**
 * The operator's copy in the reader's language, falling back to the other one
 * rather than rendering an empty banner. Mirrors `localized()` in the service so
 * an agent reading Hebrew and the customer hearing it get the same sentence.
 */
export function localizedKnownIssue(issue: KnownIssue, locale: string): { title: string; message: string } {
  const he = (locale || '').toLowerCase().startsWith('he');
  return {
    title: (he ? issue.titleHe : issue.title) || issue.title || issue.titleHe || '',
    message: (he ? issue.messageHe : issue.message) || issue.message || issue.messageHe || '',
  };
}

export const knownIssuesApi = {
  // tolerantFetch: a backend without the route 404s, and the panel says exactly
  // that instead of rendering an empty list that reads as "no incidents".
  list: (tenantCode: string, limit = 50) =>
    tolerantFetch(
      authedFetch<{ issues: KnownIssue[] }>(
        `/agent/known-issues?tenantCode=${encodeURIComponent(tenantCode)}&limit=${limit}`,
      ),
    ),
  create: (tenantCode: string, input: KnownIssueInput) =>
    authedFetch<{ ok: true; id: string }>('/agent/known-issues', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, ...input }),
    }),
  // `resolve: true` is the server's sugar for active:false — same effect, named
  // after what the operator is doing, and audited as known_issue.resolve.
  update: (tenantCode: string, id: string, patch: Partial<KnownIssueInput> & { resolve?: boolean }) =>
    authedFetch<{ ok: true }>(`/agent/known-issues/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ tenantCode, ...patch }),
    }),
};

export type ContactSortField = 'name' | 'createdAt' | 'lastSeenAt' | 'email';

export interface ContactsPage {
  items: ContactRow[];
  nextCursor: string | null;
  /**
   * The sort the server actually applied. `scope: 'page'` means the ordering
   * holds only within the rows returned so far — with a filter chip on, the
   * server pages by date (the only indexed order) and alphabetises in memory.
   */
  sort?: { by: ContactSortField; order: 'asc' | 'desc'; scope: 'query' | 'page' };
  /** Present when the requested sort could not be served exactly as asked. */
  sortCapped?: {
    requested: { by: ContactSortField; order: 'asc' | 'desc' };
    reason?: 'in-memory-page-sort' | 'ascending-unsupported-with-filters';
  };
  /**
   * What the server did about throwaway anonymous visitors. Excluded IN THE
   * QUERY (not by dropping rows from the page), so `nextCursor` stays honest.
   */
  anonymity?: {
    /** True when `anonymous == true` rows were excluded from this query. */
    hidden: boolean;
    /** How many rows that hid. null = later page, or the count query failed. */
    hiddenCount: number | null;
    /** False = the `anonymous` marker is not populated yet; nothing was hidden. */
    markerReady: boolean;
  };
}

export const directoryApi = {
  listAgentsDirectory: (tenantCode: string) =>
    authedFetch<{ items: DirectoryAgent[] }>(
      `/agent/agents-directory?tenantCode=${encodeURIComponent(tenantCode)}`
    ).catch(() => ({ items: [] as DirectoryAgent[] })),

  listContacts: (
    tenantCode: string,
    opts: {
      q?: string;
      sortBy?: 'name' | 'createdAt' | 'lastSeenAt' | 'email';
      order?: 'asc' | 'desc';
      cursor?: string;
      limit?: number;
      verified?: 'yes' | 'no';
      blocked?: 'yes' | 'no';
      /** Omitted / 'no' = anonymous throwaway visitors are hidden (the default). */
      includeAnonymous?: 'yes' | 'no';
    } = {}
  ) => {
    const p = new URLSearchParams({ tenantCode });
    for (const [k, v] of Object.entries(opts)) {
      if (v !== undefined && v !== '') p.set(k, String(v));
    }
    return authedFetch<ContactsPage>(`/agent/contacts?${p.toString()}`);
  },

  auditSubscriberView: (tenantCode: string, agentUid: string, contactId: string) =>
    authedFetch<{ ok: true }>('/agent/audit/subscriber-view', {
      method: 'POST',
      body: JSON.stringify({ tenantCode, agentUid, contactId }),
    }).catch(() => ({ ok: true as const })),

  // Lightweight delete of a single contact doc (DELETE /agent/contact/:id).
  // Writes a 'contact.delete' audit server-side. Distinct from the super_admin
  // GDPR erasure (inboxApi.deleteContactData) which also cascades chats.
  deleteContact: (tenantCode: string, contactId: string) =>
    authedFetch<{ ok: true }>(
      `/agent/contact/${encodeURIComponent(contactId)}?tenantCode=${encodeURIComponent(tenantCode)}`,
      { method: 'DELETE' }
    ),

  syncAgentsFromAuth: () =>
    authedFetch<{ ok: true; written: number }>('/admin/agents-sync-from-auth', {
      method: 'POST',
      body: JSON.stringify({}),
    }),
};

// ── Agent guides: reference documents + the video library ────────────────
// Both are served from the function behind requireAgent(), NOT from Hosting:
// a `public` dir has no auth, and these are internal documents. See
// packages/chatolam/src/routes/guides.ts.

export interface GuideDoc {
  slug: string;
  title: string;
  subtitle: string;
  lang: string;
  source: string;
  /** mtime of the source file when the function was built. */
  updatedAt: number;
  bytes: number;
}

export interface GuideVideo {
  id: string;
  title: string;
  description: string;
  category: string;
  mimeType: string;
  size: number;
  durationSec: number;
  active: boolean;
  views: number;
  createdAt: number;
  createdBy: string;
  /** Permanent, revocable link an agent can paste to a customer. */
  shareUrl: string;
}

export const guidesApi = {
  listDocs: () => authedFetch<{ items: GuideDoc[] }>('/agent/guides/docs'),

  getDoc: (slug: string) =>
    authedFetch<{ slug: string; title: string; subtitle: string; lang: string; updatedAt: number; html: string }>(
      `/agent/guides/docs/${encodeURIComponent(slug)}`
    ),

  listVideos: (tenantCode: string) =>
    authedFetch<{ items: GuideVideo[] }>(
      `/agent/guides/videos?tenantCode=${encodeURIComponent(tenantCode)}`
    ),

  updateVideo: (
    tenantCode: string,
    id: string,
    patch: { title?: string; description?: string; category?: string; active?: boolean }
  ) =>
    authedFetch<{ ok: true }>(`/agent/guides/videos/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ tenantCode, ...patch }),
    }),

  deleteVideo: (tenantCode: string, id: string) =>
    authedFetch<{ ok: true }>(
      `/agent/guides/videos/${encodeURIComponent(id)}?tenantCode=${encodeURIComponent(tenantCode)}`,
      { method: 'DELETE' }
    ),
};

/**
 * Upload a video to the library: ask for a signed PUT, stream the file straight
 * to storage, then commit the metadata. The bytes never transit the function —
 * a 500 MB screen recording would blow its memory and timeout.
 *
 * `onProgress` is driven by XHR because fetch() still cannot report upload
 * progress, and a multi-hundred-megabyte upload with no progress bar reads as
 * a hang.
 */
export async function uploadGuideVideo(
  tenantCode: string,
  file: File,
  meta: { title: string; description?: string; category?: string; durationSec?: number },
  onProgress?: (pct: number) => void
): Promise<GuideVideo> {
  const init = await authedFetch<{
    videoId: string;
    storagePath: string;
    uploadUrl: string;
    expiresAt: number;
  }>('/agent/guides/videos/upload-init', {
    method: 'POST',
    body: JSON.stringify({
      tenantCode,
      filename: file.name,
      mimeType: file.type,
      size: file.size,
    }),
  });

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', init.uploadUrl, true);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        // Label only, never the signed URL: it carries a credential.
        : reject(xhrApiError(xhr, 'video upload'));
    xhr.onerror = () => reject(new ApiError(0, 'Upload failed — network error.'));
    xhr.send(file);
  });

  return authedFetch<GuideVideo>('/agent/guides/videos', {
    method: 'POST',
    body: JSON.stringify({
      tenantCode,
      videoId: init.videoId,
      storagePath: init.storagePath,
      mimeType: file.type,
      size: file.size,
      title: meta.title,
      description: meta.description ?? '',
      category: meta.category ?? '',
      durationSec: meta.durationSec ?? 0,
    }),
  });
}
